import { Event } from '@/types/zod.schemas';
import { EVENT_TYPES } from '@/shared/constants/event-types.v4';
import { toLogicDate, toLogicTime } from '@/utils/date-utils';

/**
 * PURE EVENT LOGIC: Domain functions for cloning, shifting, and analysis.
 * HARDENED: Standardized on toLogicDate for reliable cross-timezone simulation.
 */

export const calculateDurationShift = (event: Event, newStartTime: string) => {
    const originalStart = new Date(event.start_time).getTime();
    const originalEnd = new Date(event.end_time || event.start_time).getTime();
    const duration = originalEnd - originalStart;
    
    const nextStart = new Date(newStartTime).getTime();
    return new Date(nextStart + duration).toISOString();
};

/**
 * prepareEventClone: Creates a deep clone of an event for a target date.
 */
export const prepareEventClone = (event: any, targetDateStr: string, userId: string): Partial<Event> => {
    const originalStartStr = String(event.start_time || new Date().toISOString());
    const originalStart = new Date(originalStartStr);
    
    if (isNaN(originalStart.getTime())) {
        throw new Error(`Invalid source date format: ${originalStartStr}`);
    }

    const originalEnd = new Date(event.end_time || originalStartStr);
    const duration = Math.max(0, originalEnd.getTime() - originalStart.getTime());

    let timePart = '12:00:00.000Z';
    try {
        timePart = originalStart.toISOString().split('T')[1];
    } catch (e) {
        console.warn('Failed to parse ISO time, using noon fallback', e);
    }

    const cleanTargetDate = targetDateStr.split('T')[0];
    const newStartISO = `${cleanTargetDate}T${timePart}`;
    const newStart = new Date(newStartISO);
    
    if (isNaN(newStart.getTime())) {
        throw new Error(`Clone failed: invalid date constructed from ${cleanTargetDate}`);
    }

    const newEnd = new Date(newStart.getTime() + duration);

    return {
        user_id: userId,
        type: event.type,
        status: 'PLANNED',
        label: event.label,
        summary: event.summary,
        start_time: newStartISO,
        end_time: newEnd.toISOString(),
        payload: { ...(event.payload || {}), cloned_from: event.id },
        preview_data: event.preview_data,
        program_day_id: event.program_day_id
    };
};

/**
 * prepareReschedule: Calculates new timestamps for a moved event.
 */
export const prepareReschedule = (event: any, targetDateStr: string): Partial<Event> => {
    const originalStart = new Date(event.start_time);
    const originalEnd = new Date(event.end_time || event.start_time);
    const duration = originalEnd.getTime() - originalStart.getTime();

    const timePart = originalStart.toISOString().split('T')[1];
    const newStartISO = `${targetDateStr}T${timePart}`;
    const newStart = new Date(newStartISO);
    const newEnd = new Date(newStart.getTime() + duration);

    return {
        start_time: newStartISO,
        end_time: newEnd.toISOString()
    };
};

/**
 * analyzeNormalizationNeeds: Scans events for legacy structures or redundancies.
 */
export const analyzeNormalizationNeeds = (events: any[]) => {
    const actions: any[] = [];
    if (!events || !Array.isArray(events)) return actions;

    const recoveryEvents = events.filter(e => e.type === EVENT_TYPES.RECOVERY || e.type === 'SLEEP');

    events.forEach(e => {
        if (e.type === 'SLEEP') {
            actions.push({ type: 'UPDATE', id: e.id, payload: { type: EVENT_TYPES.RECOVERY } });
        }

        if (e.type === EVENT_TYPES.RECOVERY && e.status === 'PLANNED') {
            const eDate = toLogicDate(e.start_time);
            const hasCompleted = recoveryEvents.some(re => 
                re.status === 'COMPLETED' && toLogicDate(re.start_time) === eDate
            );
            if (hasCompleted) {
                actions.push({ type: 'DELETE', id: e.id });
            }
        }
    });

    return actions;
};

/**
 * Resolves due/overdue state for an event.
 * HARDENED: Tighter windows for 'DUE NOW' (15m before, 2h after).
 */
export const resolveEventTemporalState = (event: Event) => {
    const now = new Date();
    const todayStr = toLogicDate(now);
    const eventDateStr = toLogicDate(event.start_time);
    
    const isToday = eventDateStr === todayStr;
    const isPast = eventDateStr < todayStr;
    
    const startTime = new Date(event.start_time).getTime();
    const nowTime = now.getTime();

    // DUE NOW Logic: 
    // - 15 minutes before the start time
    // - Up to 2 hours after the start time (allowing for late starts)
    const windowStart = startTime - (15 * 60 * 1000); 
    const windowEnd = startTime + (120 * 60 * 1000); 

    const isDueNow = isToday && event.status === 'PLANNED' && nowTime >= windowStart && nowTime <= windowEnd;
    const isOverdue = (isPast || (isToday && nowTime > windowEnd)) && event.status === 'PLANNED';

    return { isDueNow, isOverdue, isToday, isPast };
};

/**
 * Filter events for a specific day.
 */
export const filterEventsByDay = (events: Event[], targetDate: Date) => {
    const dateStr = toLogicDate(targetDate);
    return events.filter(e => toLogicDate(e.start_time) === dateStr);
};
